/**
 * The limit cell of the overview with its edit (masterplan credit-limits-v3, B4; E7, E11, E14). The view shows the
 * limit; an editable cell carries the marker Roman asked for on 2026-10-23: a pencil and a dashed frame on hover and
 * when the row has the focus, tooltip «Change limit – creates a request» (`styles/clim/kit.css`, section 12). A cell the person may not
 * edit has no marker. The editor is the open cell of `grid/cellEdit.ts`: Enter continues to the request dialog
 * (mandatory reason, chain preview), Esc discards, leaving the field discards.
 */
import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import type { OverviewRow } from '../../../types/creditLimits';
import { keyFromEvent } from '../grid/cellEdit';
import { EDIT_TITLE, LOCKED_TITLE } from './limitEdit';

function limitText(row: OverviewRow): string | null {
    if (!row.hasLimit || row.creditLimitChf === null) return null;
    return Math.round(row.creditLimitChf).toLocaleString('de-CH');
}

export function LimitCellView({ row, editable, locked, onEdit }: { row: OverviewRow; editable: boolean; locked: boolean; onEdit: (row: OverviewRow) => void }) {
    const text = limitText(row);
    const value = text === null ? <span className="muted">No limit</span> : <>{text}</>;
    if (!editable) {
        return locked ? <span className="lim-cell" title={LOCKED_TITLE}>{value}</span> : value;
    }
    return (
        <span className="lim-cell" title={EDIT_TITLE}>
            <button
                type="button"
                className="lim-pencil"
                tabIndex={-1}
                aria-label={`${EDIT_TITLE}: ${row.customerName ?? row.customerCode}`}
                onClick={() => onEdit(row)}
            >
                <Pencil className="i sm" aria-hidden="true" />
            </button>
            <span className="lim-val">{value}</span>
        </span>
    );
}

interface EditorProps {
    row: OverviewRow;
    draft: string;
    error: string | null;
    onDraft: (draft: string) => void;
    /** Enter or the check: continue to the dialog. */
    onCommit: () => void;
    /** Esc, the cross or leaving the field. */
    onCancel: () => void;
}

export function LimitEditor({ row, draft, error, onDraft, onCommit, onCancel }: EditorProps) {
    const ref = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
    }, []);

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const k = keyFromEvent(e);
        if (!k || (k.key !== 'Enter' && k.key !== 'Escape')) return;
        e.preventDefault();
        e.stopPropagation();
        if (k.key === 'Enter') onCommit(); else onCancel();
    };

    // The two buttons take the mouse down without stealing the focus, so the field's blur does not discard first.
    const keep = (e: { preventDefault: () => void }) => e.preventDefault();

    return (
        <span className="lim-editor">
            <input
                ref={ref}
                className="input sm n"
                inputMode="decimal"
                aria-label={`New limit in CHF for ${row.customerName ?? row.customerCode}`}
                aria-invalid={error !== null}
                title={error ?? 'Enter continues, Esc discards'}
                placeholder={limitText(row) ?? '0'}
                value={draft}
                onChange={e => onDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={onCancel}
            />
            <button type="button" className="icon-btn sm" aria-label="Continue" title="Continue (Enter)" onMouseDown={keep} onClick={onCommit}>
                <Check className="i sm" aria-hidden="true" />
            </button>
            <button type="button" className="icon-btn sm" aria-label="Discard" title="Discard (Esc)" onMouseDown={keep} onClick={onCancel}>
                <X className="i sm" aria-hidden="true" />
            </button>
        </span>
    );
}
